/**
 * Themed Common-panel catalog.
 *
 * The math editor's Common tab swaps its 24 tiles between 8 STEM
 * themes (General + 7 audience presets). Theme catalog is mirrored
 * verbatim from Claude Design's handoff at
 * `lilia-design/ThemePickerHandoff/design_handoff_theme_picker/src/theme-picker.jsx`.
 *
 * Tile lists were curated against the math editor's existing palette
 * and token factories. Each theme keeps the 6×4 reading grid: the
 * upper-left scanning zone holds the highest-click-save tiles for
 * that audience.
 *
 * Anchors that hold across every theme (row-1 "core ops"):
 *   `=`  `+`  `−`  `·`  `a⁄b`  `√x`
 * These six are the cross-corpus core from Q5 of the research doc;
 * fixing them as row 1 means a user switching themes never loses
 * muscle memory for the most common moves.
 */
import { T, type Token } from "./tokens";

export type ThemeId =
  | "general"
  | "calculus"
  | "linalg"
  | "stats"
  | "discrete"
  | "sets"
  | "physics"
  | "cs";

export interface ThemeMeta {
  id: ThemeId;
  /** Full human name — used in the section header "COMMON · <name>". */
  name: string;
  /** Short label shown on the chip when active. */
  chip: string;
  /** Glyph that identifies the theme. */
  glyph: string;
  /** Font family for the glyph. */
  font: "serif" | "mono";
  /** Glyph font style. */
  style: "normal" | "italic";
  /** One-line tooltip on hover (mouse only). */
  tip: string;
  /** Glyph weight override. CS braces render bolder; default is 500. */
  weight?: number;
}

/**
 * Theme catalog — copied from `theme-picker.jsx` lines 13–22 per the
 * handoff's "locked in research" note. Order is the rail's left-to-right
 * order.
 */
export const THEMES: ThemeMeta[] = [
  { id: "general",  name: "General",                  chip: "General",  glyph: "✦",   font: "serif", style: "normal", tip: "Default — broad STEM mix" },
  { id: "calculus", name: "Calculus",                 chip: "Calculus", glyph: "∫",   font: "serif", style: "normal", tip: "Limits, derivatives, integrals, series" },
  { id: "linalg",   name: "Linear algebra",           chip: "Linear",   glyph: "[⋮]", font: "serif", style: "normal", tip: "Vectors, matrices, det, transpose" },
  { id: "stats",    name: "Statistics & probability", chip: "Stats",    glyph: "P",   font: "serif", style: "italic", tip: "P(·), E[·], Var, σ, μ" },
  { id: "discrete", name: "Discrete math & logic",    chip: "Discrete", glyph: "∀",   font: "serif", style: "normal", tip: "∀ ∃ ∧ ∨ ⇒ ⇔ ≡" },
  { id: "sets",     name: "Set theory",               chip: "Sets",     glyph: "ℕ",   font: "serif", style: "normal", tip: "ℕ ℤ ℚ ℝ ℂ, ⊂ ⊆, ∅" },
  { id: "physics",  name: "Physics",                  chip: "Physics",  glyph: "∇",   font: "serif", style: "normal", tip: "∂ ∇, gradient and del" },
  { id: "cs",       name: "Computer science",         chip: "CS",       glyph: "{ }", font: "mono",  style: "normal", tip: "⊕, mod, O(·)", weight: 700 },
];

const LOW_GREEK = new Set("αβγδεζηθικλμνξπρστφχψω".split(""));

export interface CommonItem {
  /** Glyph shown on the tile. */
  g: string;
  /** LaTeX command (used for tooltip + tile shortcut). */
  c: string;
  /** Plain-name (search key + hover info). */
  n: string;
  /** Italic display (Greek lowercase + variable identifiers). */
  italic: boolean;
  /** Token factory. */
  mk: () => Token;
}

const eq = (): CommonItem => ({ g: "=",   c: "=",        n: "equals", italic: false, mk: () => T.rel("=") });
const plus = (): CommonItem => ({ g: "+", c: "+",        n: "plus",   italic: false, mk: () => T.op("+") });
const minus = (): CommonItem => ({ g: "−", c: "-",       n: "minus",  italic: false, mk: () => T.op("−") });
const cdot = (): CommonItem => ({ g: "·", c: "\\cdot",   n: "cdot",   italic: false, mk: () => T.op("⋅") });
const frac = (): CommonItem => ({ g: "a⁄b", c: "\\frac", n: "fraction", italic: false, mk: () => T.frac() });
const sqrt = (): CommonItem => ({ g: "√x", c: "\\sqrt",  n: "sqrt",   italic: false, mk: () => T.sqrt() });

const pow = (): CommonItem => ({ g: "xⁿ",  c: "x^{n}",   n: "power",     italic: false, mk: () => T.subsup() });
const sub = (): CommonItem => ({ g: "xᵢ",  c: "x_{i}",   n: "subscript", italic: false, mk: () => T.subsup() });
const subsup = (): CommonItem => ({ g: "xᵢⁿ", c: "x_{i}^{n}", n: "sub+sup", italic: false, mk: () => T.subsup() });
const parens = (): CommonItem => ({ g: "( )", c: "\\left()", n: "parens", italic: false, mk: () => T.parens() });
const abs = (): CommonItem => ({ g: "|x|", c: "\\left|\\right|", n: "abs", italic: false, mk: () => T.abs() });

const arrow = (): CommonItem => ({ g: "→",   c: "\\to",   n: "to",     italic: false, mk: () => T.arrow("→") });
const approx = (): CommonItem => ({ g: "≈", c: "\\approx", n: "approx", italic: false, mk: () => T.rel("≈") });
const neq = (): CommonItem => ({ g: "≠",   c: "\\neq",  n: "neq",      italic: false, mk: () => T.rel("≠") });
const leq = (): CommonItem => ({ g: "≤",   c: "\\leq",  n: "leq",      italic: false, mk: () => T.rel("≤") });
const geq = (): CommonItem => ({ g: "≥",   c: "\\geq",  n: "geq",      italic: false, mk: () => T.rel("≥") });
const sum = (): CommonItem => ({ g: "∑",   c: "\\sum",  n: "sum",      italic: false, mk: () => T.bigop("∑") });
const integral = (): CommonItem => ({ g: "∫", c: "\\int", n: "integral", italic: false, mk: () => T.bigop("∫") });

const greek = (g: string, name: string, cmd: string): CommonItem => ({
  g, c: cmd, n: name, italic: LOW_GREEK.has(g),
  mk: () => (LOW_GREEK.has(g) ? T.greek(g) : T.upletter(g)),
});

const infty = (): CommonItem => ({ g: "∞", c: "\\infty", n: "infinity", italic: false, mk: () => T.op("∞") });

const op = (g: string, name: string, cmd: string): CommonItem => ({
  g, c: cmd, n: name, italic: false, mk: () => T.op(g),
});
const rel = (g: string, name: string, cmd: string): CommonItem => ({
  g, c: cmd, n: name, italic: false, mk: () => T.rel(g),
});
const bigop = (g: string, name: string, cmd: string): CommonItem => ({
  g, c: cmd, n: name, italic: false, mk: () => T.bigop(g),
});
const fn = (name: string, cmd: string): CommonItem => ({
  g: name, c: cmd, n: name, italic: false, mk: () => T.fn(name),
});
const upset = (g: string, name: string, cmd: string): CommonItem => ({
  g, c: cmd, n: name, italic: false, mk: () => T.upletter(g),
});
const logic = (g: string, name: string, cmd: string): CommonItem => ({
  g, c: cmd, n: name, italic: false, mk: () => T.op(g),
});
const arr = (g: string, name: string, cmd: string): CommonItem => ({
  g, c: cmd, n: name, italic: false, mk: () => T.arrow(g),
});

/**
 * General — Q10's recommendation. Stays the catch-all default.
 *
 *   Row 1 — relations + operators:  =   +   −   ·   a⁄b   √x
 *   Row 2 — structures:             xⁿ  xᵢ  xᵢⁿ  ( )   |x|   →
 *   Row 3 — relations + big ops:    ≈   ≠   ≤   ≥    ∑    ∫
 *   Row 4 — Greek + constants:      α   β   θ   λ    π    ∞
 */
function general(): CommonItem[] {
  return [
    eq(), plus(), minus(), cdot(), frac(), sqrt(),
    pow(), sub(), subsup(), parens(), abs(), arrow(),
    approx(), neq(), leq(), geq(), sum(), integral(),
    greek("α", "alpha", "\\alpha"), greek("β", "beta", "\\beta"),
    greek("θ", "theta", "\\theta"), greek("λ", "lambda", "\\lambda"),
    greek("π", "pi", "\\pi"), infty(),
  ];
}

/**
 * Calculus — limits, derivatives, integrals, series.
 *
 *   Row 1 — core:                =   +   −   ·   a⁄b   √x
 *   Row 2 — calc structures:     xⁿ  xᵢⁿ ( )  |x|  lim   →
 *   Row 3 — derivs + ints:       ∂   ∇   ∫    ∮   ∑    ∏
 *   Row 4 — calc Greek/const:    ε   δ   θ    π   Δ    ∞
 *
 * Notes:
 *   - `lim` is the function token (operator name), matching how
 *     real calculus is written.
 *   - `∮` (contour) sits beside `∫` so vector-field uses are one click.
 *   - ε and δ are top-row Greek because they're the analysis primitives.
 */
function calculus(): CommonItem[] {
  return [
    eq(), plus(), minus(), cdot(), frac(), sqrt(),
    pow(), subsup(), parens(), abs(), fn("lim", "\\lim"), arrow(),
    op("∂", "partial", "\\partial"), op("∇", "nabla", "\\nabla"),
    integral(), bigop("∮", "contour", "\\oint"), sum(), bigop("∏", "product", "\\prod"),
    greek("ε", "epsilon", "\\varepsilon"), greek("δ", "delta", "\\delta"),
    greek("θ", "theta", "\\theta"), greek("π", "pi", "\\pi"),
    greek("Δ", "Delta", "\\Delta"), infty(),
  ];
}

/**
 * Linear algebra — vectors, matrices, det, transpose, inner products.
 *
 *   Row 1 — core:                =   +   −   ·   a⁄b   √x
 *   Row 2 — matrices + structs:  xⁿ  xᵢ  xᵢⁿ  [..] →   |x|
 *   Row 3 — LA ops:              ⊗   ⊕   ∘   det  ( )  →
 *   Row 4 — vectors + spaces:    α   β   λ   ∇   ℝ    ℂ
 *
 * The matrix tile uses our existing 2×2 template — Structures tab
 * exposes 3×3 + vector for the rarer cases.
 */
function linalg(): CommonItem[] {
  return [
    eq(), plus(), minus(), cdot(), frac(), sqrt(),
    pow(), sub(), subsup(),
    { g: "[..]", c: "\\begin{pmatrix}\\end{pmatrix}", n: "matrix", italic: false, mk: () => T.matrix(2, 2) },
    abs(),
    { g: "v⃗", c: "\\vec{v}", n: "vector", italic: false, mk: () => T.vector() },
    op("⊗", "otimes", "\\otimes"), op("⊕", "oplus", "\\oplus"),
    op("∘", "circ", "\\circ"), fn("det", "\\det"),
    parens(), arrow(),
    greek("α", "alpha", "\\alpha"), greek("β", "beta", "\\beta"),
    greek("λ", "lambda", "\\lambda"), op("∇", "nabla", "\\nabla"),
    upset("ℝ", "reals", "\\mathbb{R}"), upset("ℂ", "complex", "\\mathbb{C}"),
  ];
}

/**
 * Statistics & probability — distributions, expectation, variance.
 *
 *   Row 1 — core:                =   +   −   ·   a⁄b   √x
 *   Row 2 — structures:          xⁿ  xᵢ  xᵢⁿ  ( )  |x|   →
 *   Row 3 — stat relations:      ≤   ≥   ≈   ∼   ∑    ∫
 *   Row 4 — distribution Greek:  μ   σ   λ   π   ε    α
 *
 * `∼` lives in row 3 — distribution membership ("X ∼ N(μ, σ²)") is
 * stat's most distinctive relation.
 */
function stats(): CommonItem[] {
  return [
    eq(), plus(), minus(), cdot(), frac(), sqrt(),
    pow(), sub(), subsup(), parens(), abs(), arrow(),
    leq(), geq(), approx(),
    rel("∼", "sim", "\\sim"), sum(), integral(),
    greek("μ", "mu", "\\mu"), greek("σ", "sigma", "\\sigma"),
    greek("λ", "lambda", "\\lambda"), greek("π", "pi", "\\pi"),
    greek("ε", "epsilon", "\\varepsilon"), greek("α", "alpha", "\\alpha"),
  ];
}

/**
 * Discrete math & logic — quantifiers, connectives, congruences.
 *
 *   Row 1 — core:                =   +   −   ·   a⁄b   √x
 *   Row 2 — structures:          xⁿ  xᵢ  xᵢⁿ  ( )  |x|   →
 *   Row 3 — quantifiers/connectives: ∀  ∃  ∧   ∨   ⇒   ⇔
 *   Row 4 — relations + ops:    ≡   ¬   ⊕   ⊢   ⊨   ∝
 */
function discrete(): CommonItem[] {
  return [
    eq(), plus(), minus(), cdot(), frac(), sqrt(),
    pow(), sub(), subsup(), parens(), abs(), arrow(),
    logic("∀", "forall", "\\forall"), logic("∃", "exists", "\\exists"),
    logic("∧", "wedge", "\\wedge"), logic("∨", "vee", "\\vee"),
    arr("⇒", "implies", "\\Rightarrow"), arr("⇔", "iff", "\\Leftrightarrow"),
    rel("≡", "equiv", "\\equiv"), logic("¬", "neg", "\\neg"),
    op("⊕", "oplus", "\\oplus"), logic("⊢", "vdash", "\\vdash"),
    logic("⊨", "models", "\\models"), rel("∝", "propto", "\\propto"),
  ];
}

/**
 * Set theory — membership, subset, set operations, number systems.
 *
 *   Row 1 — core:                =   +   −   ·   a⁄b   √x
 *   Row 2 — membership/subset:   ∈   ∉   ⊂   ⊆   ⊃   ∋
 *   Row 3 — set ops + objects:   ∪   ∩   ∖   ∅   𝒫   ℵ
 *   Row 4 — number sets + →:     ℕ   ℤ   ℚ   ℝ   ℂ   →
 */
function setsTheme(): CommonItem[] {
  return [
    eq(), plus(), minus(), cdot(), frac(), sqrt(),
    rel("∈", "in", "\\in"), rel("∉", "notin", "\\notin"),
    rel("⊂", "subset", "\\subset"), rel("⊆", "subseteq", "\\subseteq"),
    rel("⊃", "supset", "\\supset"), rel("∋", "ni", "\\ni"),
    op("∪", "cup", "\\cup"), op("∩", "cap", "\\cap"),
    op("∖", "setminus", "\\setminus"), upset("∅", "empty", "\\emptyset"),
    upset("𝒫", "powerset", "\\mathcal{P}"), upset("ℵ", "aleph", "\\aleph"),
    upset("ℕ", "naturals", "\\mathbb{N}"), upset("ℤ", "integers", "\\mathbb{Z}"),
    upset("ℚ", "rationals", "\\mathbb{Q}"), upset("ℝ", "reals", "\\mathbb{R}"),
    upset("ℂ", "complex", "\\mathbb{C}"), arrow(),
  ];
}

/**
 * Physics — derivatives, vector calculus, common Greek + Δ.
 *
 *   Row 1 — core:                =   +   −   ·   a⁄b   √x
 *   Row 2 — structures:          xⁿ  xᵢ  xᵢⁿ  ( )  |x|   →
 *   Row 3 — derivs + integrals:  ∂   ∇   Δ    ∫   ∮    ∑
 *   Row 4 — physics constants:   π   ω   λ    ε   μ    α
 */
function physics(): CommonItem[] {
  return [
    eq(), plus(), minus(), cdot(), frac(), sqrt(),
    pow(), sub(), subsup(), parens(), abs(), arrow(),
    op("∂", "partial", "\\partial"), op("∇", "nabla", "\\nabla"),
    op("Δ", "Delta", "\\Delta"), integral(),
    bigop("∮", "contour", "\\oint"), sum(),
    greek("π", "pi", "\\pi"), greek("ω", "omega", "\\omega"),
    greek("λ", "lambda", "\\lambda"), greek("ε", "epsilon", "\\varepsilon"),
    greek("μ", "mu", "\\mu"), greek("α", "alpha", "\\alpha"),
  ];
}

/**
 * Computer science — bitwise ops, congruence/modular, complexity.
 *
 *   Row 1 — core:                =   +   −   ·   a⁄b   √x
 *   Row 2 — structures:          xⁿ  xᵢ  xᵢⁿ  ( )  |x|   →
 *   Row 3 — CS ops + rel:        ⊕   ⊗   ∘   ≡   ≪   ≫
 *   Row 4 — sets + fns:          ∈   ∅   log lim   ∀    ∃
 */
function cs(): CommonItem[] {
  return [
    eq(), plus(), minus(), cdot(), frac(), sqrt(),
    pow(), sub(), subsup(), parens(), abs(), arrow(),
    op("⊕", "xor", "\\oplus"), op("⊗", "otimes", "\\otimes"),
    op("∘", "compose", "\\circ"), rel("≡", "equiv", "\\equiv"),
    rel("≪", "ll", "\\ll"), rel("≫", "gg", "\\gg"),
    rel("∈", "in", "\\in"), upset("∅", "empty", "\\emptyset"),
    fn("log", "\\log"), fn("lim", "\\lim"),
    logic("∀", "forall", "\\forall"), logic("∃", "exists", "\\exists"),
  ];
}

const BUILDERS: Record<ThemeId, () => CommonItem[]> = {
  general,
  calculus,
  linalg,
  stats,
  discrete,
  sets: setsTheme,
  physics,
  cs,
};

/**
 * Build the 24-tile Common list for the given theme. Always returns
 * exactly 24 entries — themes that don't naturally fill 24 tiles are
 * padded with the General row-1 anchors (cross-corpus core).
 */
export function buildCommonItems(theme: ThemeId): CommonItem[] {
  const items = BUILDERS[theme]();
  return items;
}

const STORAGE_KEY = "lilia.math.commonTheme";

/** Read the persisted theme, defaulting to `general` if stale or absent. */
export function loadCommonTheme(): ThemeId {
  if (typeof window === "undefined") return "general";
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    const hit = THEMES.find((t) => t.id === saved);
    return hit ? hit.id : "general";
  } catch {
    return "general";
  }
}

/** Persist the chosen theme. Silently swallows quota errors. */
export function saveCommonTheme(theme: ThemeId): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Ignore — privacy mode / quota exceeded shouldn't break the UI.
  }
}

const PULSE_KEY = "lilia.math.themeRail.pulseSeen";

/** Did this user already see the first-mount pulse? */
export function hasSeenPulse(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(PULSE_KEY) === "1";
  } catch {
    return true;
  }
}

/** Mark the pulse as seen so it doesn't replay on every mount. */
export function markPulseSeen(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PULSE_KEY, "1");
  } catch {
    // Ignore.
  }
}
